import { Card } from '@payaca/ambrosia';
import { FC, PropsWithChildren, useMemo, type JSX } from 'react';

import DropdownField from '@payaca/components/dropdownField/DropdownField';
import SwitchField from '@payaca/components/switchField/SwitchField';
import UntitledIcon from '@payaca/untitled-icons';

import { useBusinessAccountForm } from './useBusinessAccountForm';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import SettingsSaveButton from './SettingsSaveButton';
import { AccountRegions } from '@payaca/types/accountTypes';

type Props = {
  readOnly: boolean;
};

type FormState = {
  isCisSubcontractor: boolean;
  cisDeductionRate: number | null;
};

/** True when the account is in a region that operates a CIS-style scheme. */
export const useHasCis = (): boolean => {
  const region = useRegion();
  return useMemo(() => [AccountRegions.UK].includes(region), [region]);
};

const CompanySettingsCis: FC<Props> = ({
  readOnly,
}: Props): JSX.Element | null => {
  const account = useAccount();
  const hasCis = useHasCis();

  const isConnectedToQuickbooks = account.integrations?.quickbooks;
  const isConnectedToXero = account.integrations?.xero;

  const initialState: FormState = {
    isCisSubcontractor: account.isCisSubcontractor ?? false,
    cisDeductionRate: account.cisDeductionRate ?? 0,
  };

  const {
    formState,
    updateFormFields,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, {});

  if (!hasCis) return null;

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-2">
        <h2>Construction industry scheme</h2>
        <SwitchField
          name="isCisSubcontractor"
          value={formState.isCisSubcontractor}
          onChange={(value: { [isCisSubcontractor: string]: boolean }) => {
            updateFormFields({
              ...value,
              cisDeductionRate: value.isCisSubcontractor ? 20 : null,
            });
          }}
          label="I am a CIS subcontractor"
          isDisabled={readOnly}
        />
        {formState.isCisSubcontractor && (
          <DropdownField
            name="cisDeductionRate"
            isDisabled={readOnly}
            value={formState.cisDeductionRate}
            label={'CIS deduction rate'}
            onChange={updateFormFields}
            options={[
              {
                label: '0%',
                value: 0,
              },
              {
                label: '20%',
                value: 20,
              },
              {
                label: '30%',
                value: 30,
              },
            ]}
          />
        )}
        {formState.isCisSubcontractor && (
          <CisError>
            <p>
              If you change the CIS deduction rate for this company, CIS
              deduction rates will be updated against all applicable line items
              and against any quotes and invoices which have not been sent. The
              old CIS deduction rate will remain on any sent quotes and
              invoices.
            </p>
          </CisError>
        )}
        {isConnectedToQuickbooks && formState.isCisSubcontractor && (
          <CisError>
            <p>
              Your account is connected to Quickbooks, but our Quickbooks
              integration does not currently support CIS behaviours.
            </p>
          </CisError>
        )}
        {isConnectedToXero && formState.isCisSubcontractor && (
          <CisError>
            <p>
              Your account is connected to Xero. You must have CIS behaviours
              enabled in Xero for the integration to function. Please{' '}
              <strong>
                <a
                  href="https://central.xero.com/s/article/Enable-CIS-in-your-organisation"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  follow these instructions
                </a>
              </strong>{' '}
              to enable CIS for your organisation in Xero if you haven&#39;t
              already.
            </p>
            <p>
              Make sure your CIS subcontractor deduction rates in Xero and
              Payaca match.
            </p>
          </CisError>
        )}
        {!readOnly && (
          <SettingsSaveButton
            isDirty={isDirty}
            label="Save CIS settings"
            isSaving={isSaving}
            isValid={isValid}
            onSave={saveAndFlush}
          />
        )}
      </Card.Body>
    </Card>
  );
};

type CisErrorProps = {
  doShow?: boolean;
};
const CisError: FC<PropsWithChildren<CisErrorProps>> = ({
  children,
  doShow,
}: PropsWithChildren<CisErrorProps>): JSX.Element => {
  return (
    <div className={`cis-error${doShow ? ' show' : ''}`}>
      <UntitledIcon name="info-circle.3" />
      <div>{children}</div>
    </div>
  );
};

export default CompanySettingsCis;
